import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Loader2, Camera, ScanLine } from "lucide-react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { getCatMeta } from "../lib/categoryMeta";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const emptyForm = () => ({
  type: "income",
  amount: "",
  category: "",
  description: "",
  date: new Date().toISOString().slice(0, 10),
});

export default function TransactionModal({ open, onOpenChange, categories, editing, onSaved, onCategoriesChanged }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [receiptId, setReceiptId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [addingCat, setAddingCat] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [extraCats, setExtraCats] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...editing, amount: String(editing.amount) } : emptyForm());
      setReceiptId(editing?.receipt_id || null);
      setPreview(editing?.receipt_id ? `${API}/receipts/${editing.receipt_id}` : null);
    }
  }, [open, editing]);

  const handleScan = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/receipts/scan", fd);
      setReceiptId(data.receipt_id);
      const x = data.extracted || {};
      setForm((f) => ({
        ...f,
        type: x.type || f.type,
        amount: x.amount != null ? String(x.amount) : f.amount,
        category: x.category || "",
        date: x.date || f.date,
        description: x.description || f.description,
      }));
      if (x.amount != null) toast.success("Fiş okundu, bilgiler dolduruldu. Kontrol edip kaydedin.");
      else toast.info("Fotoğraf eklendi ancak tutar okunamadı, bilgileri elle girin.");
    } catch (err) {
      setPreview(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setScanning(false);
    }
  };

  const baseCats = form.type === "income" ? categories.income : categories.expense;
  const catList = [...baseCats, ...extraCats.filter((e) => e.type === form.type && !baseCats.includes(e.name)).map((e) => e.name)];

  const setType = (type) => setForm({ ...form, type, category: "" });

  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    try {
      await api.post("/categories", { type: form.type, name });
      setExtraCats((x) => [...x, { type: form.type, name }]);
      toast.success(`"${name}" kategorisi eklendi`);
      setForm((f) => ({ ...f, category: name }));
      setNewCat("");
      setAddingCat(false);
      onCategoriesChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category) {
      toast.error("Lütfen bir kategori seçin");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        amount: parseFloat(form.amount),
        category: form.category,
        description: form.description,
        date: form.date,
        receipt_id: receiptId,
      };
      if (editing) {
        await api.put(`/transactions/${editing.id}`, payload);
        toast.success("İşlem güncellendi");
      } else {
        await api.post("/transactions", payload);
        toast.success("İşlem eklendi");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="transaction-modal" className="bg-card border-border sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "İşlemi Düzenle" : "Yeni İşlem Ekle"}</DialogTitle>
          <DialogDescription className="sr-only">Gelir veya gider işlemi girin</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
            className="hidden" onChange={handleScan} data-testid="receipt-file-input" />
          <div className="flex items-center gap-3">
            <button type="button" data-testid="scan-receipt-button" onClick={() => fileInputRef.current?.click()}
              disabled={scanning}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold border border-dashed border-primary/60 text-primary hover:bg-primary/10 transition-colors disabled:opacity-60">
              {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : preview ? <ScanLine className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
              {scanning ? "Fiş okunuyor..." : preview ? "Farklı fiş tara" : "Fiş / Fatura Tara (Fotoğraf)"}
            </button>
            {preview && (
              <img src={preview} alt="Fiş önizleme" data-testid="receipt-preview-image"
                className="w-12 h-12 rounded-lg object-cover border border-border" />
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="type-income-button" onClick={() => setType("income")}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold border transition-colors ${
                form.type === "income" ? "bg-emerald-500/15 border-emerald-500 text-emerald-400" : "border-border text-muted-foreground hover:bg-secondary"
              }`}>
              <TrendingUp className="w-4 h-4" /> Gelir
            </button>
            <button type="button" data-testid="type-expense-button" onClick={() => setType("expense")}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold border transition-colors ${
                form.type === "expense" ? "bg-red-500/15 border-red-500 text-red-400" : "border-border text-muted-foreground hover:bg-secondary"
              }`}>
              <TrendingDown className="w-4 h-4" /> Gider
            </button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tx-amount">Tutar (₺)</Label>
            <Input id="tx-amount" data-testid="transaction-amount-input" type="number" step="0.01" min="0.01" required
              placeholder="0,00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>

          <div className="space-y-2">
            <Label>Kategori</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="transaction-category-select">
                <SelectValue placeholder="Kategori seçin" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {catList.map((c) => {
                  const { Icon, color } = getCatMeta(categories.meta, c, form.type);
                  return (
                    <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>
                      <span className="flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color }} />
                        {c}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {addingCat ? (
              <div className="flex gap-2">
                <Input data-testid="new-category-input" placeholder="Yeni kategori adı" maxLength={40} autoFocus
                  value={newCat} onChange={(e) => setNewCat(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCategory())} />
                <Button type="button" data-testid="new-category-save" variant="outline" onClick={addCategory}
                  disabled={!newCat.trim()} className="rounded-full border-border shrink-0">Ekle</Button>
                <Button type="button" variant="ghost" onClick={() => { setAddingCat(false); setNewCat(""); }}
                  className="rounded-full shrink-0 text-muted-foreground">Vazgeç</Button>
              </div>
            ) : (
              <button type="button" data-testid="add-category-toggle" onClick={() => setAddingCat(true)}
                className="text-xs text-primary hover:text-accent transition-colors">
                + Yeni kategori ekle
              </button>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="tx-date">Tarih</Label>
            <Input id="tx-date" data-testid="transaction-date-input" type="date" required
              value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tx-desc">Açıklama (isteğe bağlı)</Label>
            <Textarea id="tx-desc" data-testid="transaction-description-input" rows={2}
              placeholder="Örn: Market alışverişi" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>

          <Button data-testid="transaction-save-button" type="submit" disabled={saving} className="w-full rounded-full font-semibold">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editing ? "Güncelle" : "Kaydet"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
